/**
 * Turning an inbreeding coefficient into pairing advice.
 *
 * The coefficient itself is **not** computed here. Wright's F is domain logic and lives
 * in BreeNova.Domain, computed server-side and returned by the API — reimplementing it
 * in TypeScript would give two answers to a question that must only have one. What this
 * module does is decide how to *present* a number the server already produced.
 */

/** How concerned a breeder should be about a proposed pairing. */
export type InbreedingBand = "unrelated" | "acceptable" | "caution" | "avoid";

export interface InbreedingAdvice {
  band: InbreedingBand;
  /** The coefficient as a percentage, rounded for display. */
  percent: number;
  /** Short label for a status pill. */
  label: string;
}

/**
 * Thresholds in coefficient terms, so the reasoning is legible rather than magic:
 * - 0.0625 is full first cousins.
 * - 0.125 is half siblings, or a grandparent paired back to a grandchild.
 * - 0.25 is full siblings, or a parent paired back to its own offspring.
 */
const FIRST_COUSINS = 0.0625;
const HALF_SIBLINGS = 0.125;
const FULL_SIBLINGS = 0.25;

/**
 * Bands a coefficient for display. Line-breeding is a legitimate technique, so nothing
 * here forbids a pairing — the strongest word is "avoid", and the decision stays the
 * breeder's.
 *
 * @param coefficient Wright's F from the API, in the range 0..1.
 * @throws RangeError if the value is not a coefficient.
 */
export function adviseOnInbreeding(coefficient: number): InbreedingAdvice {
  if (!Number.isFinite(coefficient) || coefficient < 0 || coefficient > 1) {
    throw new RangeError(`Inbreeding coefficient must be between 0 and 1, got ${coefficient}.`);
  }

  const percent = Math.round(coefficient * 1000) / 10;

  if (coefficient === 0) {
    return { band: "unrelated", percent, label: "Unrelated" };
  }

  if (coefficient < FIRST_COUSINS) {
    return { band: "acceptable", percent, label: `${percent}% — distant` };
  }

  if (coefficient < HALF_SIBLINGS) {
    return { band: "caution", percent, label: `${percent}% — cousins` };
  }

  if (coefficient < FULL_SIBLINGS) {
    return { band: "caution", percent, label: `${percent}% — close` };
  }

  return { band: "avoid", percent, label: `${percent}% — very close` };
}

/**
 * A note explaining what the number means in birds, not in statistics. Breeders think in
 * relationships ("that's her brother"), so the advice reads back in those terms.
 */
export function describeRelatedness(coefficient: number): string {
  const { band } = adviseOnInbreeding(coefficient);

  switch (band) {
    case "unrelated":
      return "No shared ancestry in the recorded pedigree.";
    case "acceptable":
      return "Distantly related. Comparable to a pairing outside the line.";
    case "caution":
      return "Closely related — around cousin to half-sibling. Line-breeding territory.";
    case "avoid":
      return "As related as full siblings or a parent and its own chick.";
  }
}
